import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import Heading from '@tiptap/extension-heading';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import ListItem from '@tiptap/extension-list-item';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import { 
  Bold as BoldIcon, 
  Italic as ItalicIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  ListOrdered,
  Type
} from 'lucide-react';

const RichTextEditor = ({ content, onChange }) => {
  const editor = useEditor({
    extensions: [
      Document,
      Paragraph,
      Text,
      Bold,
      Italic,
      Color,
      TextStyle,
      Heading.configure({
        levels: [1, 2, 3]
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      BulletList,
      OrderedList,
      ListItem,
      StarterKit.configure({
        document: false,
        text: false,
        paragraph: false,
        bold: false,
        italic: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
      })
    ],
    content: content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  if (!editor) {
    return null;
  }

  const ColorButton = ({ color, title }) => (
    <button
      onClick={() => editor.chain().focus().setColor(color).run()}
      className={`w-8 h-8 rounded-lg border ${
        editor.isActive('textStyle', { color }) ? 'ring-2 ring-offset-2 ring-blue-500' : ''
      }`}
      style={{ backgroundColor: color }}
      title={title}
    />
  );

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="bg-gray-50 p-2 border-b flex flex-wrap gap-2">
        {/* Text Style Controls */}
        <div className="flex items-center gap-1 border-l pl-2">
          <button
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={`p-1.5 rounded hover:bg-gray-200 ${
              editor.isActive('bold') ? 'bg-gray-200' : ''
            }`}
            title="מודגש"
          >
            <BoldIcon className="w-5 h-5" />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={`p-1.5 rounded hover:bg-gray-200 ${
              editor.isActive('italic') ? 'bg-gray-200' : ''
            }`}
            title="נטוי"
          >
            <ItalicIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Heading Controls */}
        <div className="flex items-center gap-1 border-l pl-2">
          <select
            onChange={(e) => {
              if (e.target.value === 'paragraph') {
                editor.chain().focus().setParagraph().run();
              } else {
                editor.chain().focus().toggleHeading({ level: parseInt(e.target.value) }).run();
              }
            }}
            className="p-1.5 rounded bg-white border"
            value={
              editor.isActive('heading', { level: 1 })
                ? '1'
                : editor.isActive('heading', { level: 2 })
                ? '2'
                : editor.isActive('heading', { level: 3 })
                ? '3'
                : 'paragraph'
            }
          >
            <option value="paragraph">פסקה רגילה</option>
            <option value="1">כותרת 1</option>
            <option value="2">כותרת 2</option>
            <option value="3">כותרת 3</option>
          </select>
        </div>

        {/* Alignment Controls */}
        <div className="flex items-center gap-1 border-l pl-2">
          <button
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            className={`p-1.5 rounded hover:bg-gray-200 ${
              editor.isActive({ textAlign: 'right' }) ? 'bg-gray-200' : ''
            }`}
            title="יישור לימין"
          >
            <AlignRight className="w-5 h-5" />
          </button>
          <button
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            className={`p-1.5 rounded hover:bg-gray-200 ${
              editor.isActive({ textAlign: 'center' }) ? 'bg-gray-200' : ''
            }`}
            title="מרכוז"
          >
            <AlignCenter className="w-5 h-5" />
          </button>
          <button
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            className={`p-1.5 rounded hover:bg-gray-200 ${
              editor.isActive({ textAlign: 'left' }) ? 'bg-gray-200' : ''
            }`}
            title="יישור לשמאל"
          >
            <AlignLeft className="w-5 h-5" />
          </button>
        </div>

        {/* List Controls */}
        <div className="flex items-center gap-1 border-l pl-2">
          <button
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={`p-1.5 rounded hover:bg-gray-200 ${
              editor.isActive('bulletList') ? 'bg-gray-200' : ''
            }`}
            title="רשימה"
          >
            <List className="w-5 h-5" />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={`p-1.5 rounded hover:bg-gray-200 ${
              editor.isActive('orderedList') ? 'bg-gray-200' : ''
            }`}
            title="רשימה ממוספרת"
          >
            <ListOrdered className="w-5 h-5" />
          </button>
        </div>

        {/* Color Controls */}
        <div className="flex items-center gap-1">
          <ColorButton color="#000000" title="שחור" />
          <ColorButton color="#2563EB" title="כחול" />
          <ColorButton color="#16A34A" title="ירוק" />
          <ColorButton color="#DC2626" title="אדום" />
        </div>
      </div>

      {/* Editor Content */}
      <EditorContent 
        editor={editor} 
        className="prose max-w-none p-4 min-h-[200px] focus:outline-none"
        dir="rtl"
      />
    </div>
  );
};

export default RichTextEditor;